import type { WebflowScriptAsset } from '@/lib/webflow-poc/extract-content';

const LOADED_SRC_ATTR = 'data-tradewing-webflow-script-src';
const INJECTED_ATTR = 'data-tradewing-webflow-script';

const hasScriptWithSrc = (src: string): boolean =>
  Array.from(document.querySelectorAll('script[src]')).some((node) => {
    if (!(node instanceof HTMLScriptElement)) return false;
    return (
      node.getAttribute(LOADED_SRC_ATTR) === src ||
      node.getAttribute('src') === src ||
      node.src === src
    );
  });

const loadExternalScript = (script: WebflowScriptAsset): Promise<void> => {
  if (!script.src) {
    return Promise.resolve();
  }

  if (hasScriptWithSrc(script.src)) {
    // Already present from a prior page — treat as loaded.
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = script.src!;
    el.setAttribute(LOADED_SRC_ATTR, script.src!);
    el.setAttribute(INJECTED_ATTR, 'external');
    if (script.type) el.type = script.type;
    if (script.async) el.async = true;
    else el.async = false; // preserve document order
    if (script.defer) el.defer = true;
    if (script.id) el.id = script.id;
    if (script.crossOrigin) el.crossOrigin = script.crossOrigin;
    if (script.integrity) el.integrity = script.integrity;
    if (script.referrerPolicy) {
      el.referrerPolicy = script.referrerPolicy as ReferrerPolicy;
    }
    if (script.nomodule) el.noModule = true;

    el.onload = () => resolve();
    el.onerror = () =>
      reject(new Error(`Failed to load Webflow script: ${script.src}`));
    document.body.appendChild(el);
  });
};

const runInlineScript = (script: WebflowScriptAsset): void => {
  if (!script.content) return;

  const el = document.createElement('script');
  el.setAttribute(INJECTED_ATTR, 'inline');
  if (script.type) el.type = script.type;
  if (script.id) el.id = script.id;
  el.text = script.content;
  document.body.appendChild(el);
  // Inline scripts execute synchronously on append; remove node to avoid clutter.
  el.remove();
};

export const reinitWebflowRuntime = (): void => {
  const webflow = (window as unknown as { Webflow?: any }).Webflow;
  if (!webflow) return;

  try {
    if (typeof webflow.destroy === 'function') {
      webflow.destroy();
    }
  } catch {
    // ignore destroy errors from prior soft navigations
  }

  try {
    if (typeof webflow.ready === 'function') {
      webflow.ready();
    }
  } catch {
    // ignore
  }

  try {
    if (typeof webflow.require === 'function') {
      const ix2 = webflow.require('ix2');
      if (ix2 && typeof ix2.init === 'function') {
        ix2.init();
      }
    }
  } catch {
    // IX2 may not be present on every page
  }

  try {
    // Some Webflow builds expose a global push/ready queue.
    if (Array.isArray(webflow) && typeof (webflow as any).push === 'function') {
      (webflow as any).push(() => undefined);
    }
  } catch {
    // ignore
  }
};

/**
 * Inject Webflow runtime + embed scripts in document order.
 * External scripts are loaded once; inline scripts re-run on each page so IX2
 * / custom embeds rebind to the new content island.
 */
export const injectWebflowScripts = async (
  scripts: WebflowScriptAsset[]
): Promise<() => void> => {
  let cancelled = false;

  for (const script of scripts) {
    if (cancelled) break;

    try {
      if (script.src) {
        await loadExternalScript(script);
      } else if (script.content) {
        runInlineScript(script);
      }
    } catch (error) {
      console.error('Webflow script injection failed', error);
    }
  }

  if (!cancelled) {
    // Allow layout to settle, then kick Webflow interactions.
    requestAnimationFrame(() => {
      if (!cancelled) {
        reinitWebflowRuntime();
      }
    });
  }

  return () => {
    cancelled = true;
  };
};

/**
 * Load jQuery + webflow.js for dynamic-page nav chrome, then re-bind w-nav.
 * No-ops when scriptSrcs is empty. Dedupes via injectWebflowScripts.
 */
export const injectWebflowNavRuntime = async (
  scriptSrcs: string[]
): Promise<() => void> => {
  const scripts: WebflowScriptAsset[] = (scriptSrcs || [])
    .filter(Boolean)
    .map((src) => ({ src, async: false }));

  if (scripts.length === 0) {
    // Runtime may already be present from a prior static page — still rebind.
    requestAnimationFrame(() => reinitWebflowRuntime());
    return () => undefined;
  }

  document.documentElement.classList.add('w-mod-js');
  return injectWebflowScripts(scripts);
};
